/**
 * HomeSkeleton
 * ------------------------------------------------------------------
 * Shown on the Home view when the user has an authenticated Tesla
 * session but Fleet API data hasn't merged yet. Keeps the "no mock
 * data" philosophy end-to-end — instead of showing demo numbers below
 * the "Connecting to Tesla…" header, we render shimmering silhouettes
 * where the score dial, chart, and stat cards will land.
 *
 * Once `vehicle.live` flips true, HomeView swaps this out for the real
 * content in a single render — no layout shift because the silhouettes
 * are sized to match the finished tiles.
 */

import React from "react";

const shimmer =
  "bg-gradient-to-r from-white/[0.03] via-white/[0.06] to-white/[0.03] bg-[length:200%_100%] animate-[shimmer_1.6s_linear_infinite]";

export const HomeSkeleton = () => (
  <div className="space-y-4" data-testid="home-skeleton">
    {/* Score dial silhouette */}
    <div className="text-center">
      <div className={`h-3 w-56 mx-auto rounded ${shimmer}`} />
      <div className="relative inline-block mt-4">
        <div className={`w-[200px] h-[200px] rounded-full ${shimmer}`} style={{ maskImage: "radial-gradient(circle at center, transparent 55%, black 58%, black 68%, transparent 71%)", WebkitMaskImage: "radial-gradient(circle at center, transparent 55%, black 58%, black 68%, transparent 71%)" }} />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
          <div className={`h-9 w-16 rounded ${shimmer}`} />
          <div className={`h-2 w-12 rounded ${shimmer}`} />
        </div>
      </div>
      <div className={`h-3 w-40 mx-auto mt-3 rounded ${shimmer}`} />
    </div>

    {/* Battery health chart silhouette */}
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <div className="flex items-baseline justify-between mb-3 px-1">
        <div className={`h-2.5 w-28 rounded ${shimmer}`} />
        <div className={`h-2.5 w-16 rounded ${shimmer}`} />
      </div>
      <div className={`h-[120px] w-full rounded ${shimmer}`} />
    </div>

    {/* 4 StatCard silhouettes */}
    <div className="grid grid-cols-2 gap-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <div className={`h-2 w-16 rounded ${shimmer}`} />
          <div className={`h-6 w-20 mt-2 rounded ${shimmer}`} />
          <div className={`h-2 w-14 mt-2 rounded ${shimmer}`} />
        </div>
      ))}
    </div>

    {/* Sub-score radar silhouette */}
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <div className="flex items-baseline justify-between mb-3 px-1">
        <div className={`h-2.5 w-32 rounded ${shimmer}`} />
        <div className={`h-2.5 w-16 rounded ${shimmer}`} />
      </div>
      <div className={`h-[220px] w-full rounded ${shimmer}`} />
    </div>

    {/* Warranty forecast silhouette */}
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <div className={`h-2.5 w-24 rounded ${shimmer}`} />
      <div className={`h-4 w-3/4 mt-2 rounded ${shimmer}`} />
      <div className={`h-3 w-1/2 mt-2 rounded ${shimmer}`} />
    </div>

    {/* Predictor silhouette */}
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <div className={`h-3 w-32 rounded ${shimmer}`} />
      <div className={`h-5 w-3/4 mt-2 rounded ${shimmer}`} />
      <div className={`h-3 w-2/3 mt-2 rounded ${shimmer}`} />
      <div className={`h-10 w-full mt-4 rounded-lg ${shimmer}`} />
    </div>
  </div>
);
